import React, { useMemo } from 'react';

import { queryParameterContext } from './QueryParameterContext';
import { useConversionTables, ConversionTablesSearchQuery } from '../data/useConversionTables';
import type { ConversionTableResource } from '@ab/ams-school-api/types/private/v1';

type SearchResult = {
  conversionTables: ConversionTableResource[];
  totalConversionTables: number;
  isFetchingConversionTables: boolean;
  conversionTablesError?: unknown;
};

const searchResultContext = React.createContext<SearchResult | undefined>(undefined);

export function SearchResultContextProvider(props: { children: React.ReactNode }) {
  const { parameters } = queryParameterContext.useQueryParameter();
  const query = useMemo<ConversionTablesSearchQuery>(() => {
    return {
      'page[size]': parameters.pageSize,
      'page[number]': parameters.pageNumber,
      sort: parameters.sort,
    };
  }, [parameters.pageNumber, parameters.pageSize, parameters.sort]);

  const { conversionTables, totalConversionTables, isFetchingConversionTables, conversionTablesError } =
    useConversionTables(query);
  const value = useMemo(
    () => ({
      conversionTables,
      totalConversionTables,
      isFetchingConversionTables,
      conversionTablesError,
    }),
    [conversionTables, totalConversionTables, isFetchingConversionTables, conversionTablesError]
  );

  return <searchResultContext.Provider value={value}>{props.children}</searchResultContext.Provider>;
}

export function useSearchResult() {
  const value = React.useContext(searchResultContext);
  if (value === undefined) {
    throw new Error('useSearchResult must be used within a SearchResultContextProvider');
  }
  return value;
}
